const FILE_ITEM_PREFIX = 'file:'
const MAIN_FILE_ITEM_PREFIX = `${FILE_ITEM_PREFIX}main:`
const NOTE_PLACEHOLDER_ITEM = { id: 'Note' }

const GENERATED_FILE_PREFIXES = [
  'novelty-analysis:'
]

const DEFAULT_FILE_ICON = 'application/note.png'

const normalizeFileToken = (value, fallback = 'file') => {
  const source = String(value || fallback)
  return encodeURIComponent(source)
}

const getFileExtension = (name = '') => {
  const normalized = String(name || '')
  const lastDotIndex = normalized.lastIndexOf('.')
  if (lastDotIndex === -1) {
    return ''
  }
  return normalized.slice(lastDotIndex + 1).toLowerCase()
}

export const isGeneratedStepFile = (file = {}) => {
  const fileId = String(file?.id || '')
  return GENERATED_FILE_PREFIXES.some(prefix => fileId.startsWith(prefix))
}

export const isUploadManagedStepFile = (file = {}) => {
  return !isGeneratedStepFile(file)
}

export const buildRuntimeFileId = (fileKind, file = {}, index = 0) => {
  const baseToken = file?.modelUploadId || file?.id || file?.filePath || `${file?.fileName || 'file'}-${index}`
  return `${FILE_ITEM_PREFIX}${fileKind}:${normalizeFileToken(baseToken, `${fileKind}-${index}`)}`
}

export const isRuntimeFileItem = (item) => {
  return Boolean(
    item &&
    (
      item.itemType === 'file' ||
      String(item?.id || '').startsWith(FILE_ITEM_PREFIX)
    )
  )
}

const isMainRuntimeFileItem = (item) => {
  if (!item) {
    return false
  }

  if (item.itemType === 'file' && item.fileKind === 'main') {
    return true
  }

  return String(item?.id || '').startsWith(MAIN_FILE_ITEM_PREFIX)
}

const createRuntimeFileItem = (file, fileKind, index, viewerData = null) => {
  const fileName = file?.fileName || `文件${index + 1}`
  const fileExt = getFileExtension(fileName || file?.filePath)
  const runtimeId = buildRuntimeFileId(fileKind, file, index)
  const hasParsedContent = Boolean(viewerData?.docResult)
  const sourceLabelMap = {
    main: '主要文件',
    reference: '参考文件',
    generated: '过程文件',
  }

  return {
    id: runtimeId,
    itemType: 'file',
    type: '文件类',
    name: fileName,
    icon: DEFAULT_FILE_ICON,
    description: `${sourceLabelMap[fileKind] || '文件'}，使用查阅批注样式打开`,
    componentId: fileKind === 'main' && hasParsedContent ? 'Note' : 'FileViewer',
    fileKind,
    fileName,
    fileExt,
    sourceLabel: sourceLabelMap[fileKind] || '文件',
    previewPath: file?.previewPath || file?.fileUrl || '',
    fileUrl: file?.fileUrl || '',
    filePath: file?.filePath || '',
    modelUploadId: file?.modelUploadId || '',
    viewerData,
    fileMeta: file || {},
  }
}

export const buildStep2FileItems = (step2Info = {}) => {
  const mainFiles = Array.isArray(step2Info?.step2AttachmentMain) ? step2Info.step2AttachmentMain : []
  const referenceFiles = Array.isArray(step2Info?.step2AttachmentReference) ? step2Info.step2AttachmentReference : []
  const annotationData = step2Info?.step2Component?.comp_ann || null

  return [
    ...mainFiles.map((file, index) => createRuntimeFileItem(file, 'main', index, annotationData)),
    ...referenceFiles.map((file, index) => createRuntimeFileItem(
      file,
      isGeneratedStepFile(file) ? 'generated' : 'reference',
      index,
      file?.viewerData || null
    )),
  ]
}

export const findRuntimeFileItem = (fileItems = [], itemId = '') => {
  return (fileItems || []).find(item => item.id === itemId) || null
}

export const workspaceHasItem = (workspaceData = [], itemId = '') => {
  if (!Array.isArray(workspaceData) || !itemId) {
    return false
  }

  return workspaceData.some(row => (
    Array.isArray(row) &&
    row.some(cell => (
      Array.isArray(cell?.items) &&
      cell.items.some(item => item?.id === itemId)
    ))
  ))
}

export const sanitizeWorkspaceDataWithFiles = (sourceData = [], fileItems = []) => {
  if (!Array.isArray(sourceData)) {
    return []
  }

  const availableIds = new Set((fileItems || []).map(item => item.id))
  const mainFileItem = (fileItems || []).find(item => item?.fileKind === 'main') || null
  let changed = false

  const nextData = sourceData.map(row => {
    if (!Array.isArray(row)) {
      changed = true
      return []
    }

    return row.map(cell => {
      if (!cell || !Array.isArray(cell.items)) {
        return cell
      }

      const nextItems = cell.items.reduce((result, item) => {
        if (item?.id === 'Note' && mainFileItem) {
          changed = true
          result.push(mainFileItem)
          return result
        }

        if (!isRuntimeFileItem(item)) {
          result.push(item)
          return result
        }

        if (availableIds.has(item.id)) {
          result.push(item)
          return result
        }

        if (isMainRuntimeFileItem(item)) {
          changed = true
          result.push(mainFileItem || NOTE_PLACEHOLDER_ITEM)
          return result
        }

        changed = true
        return result
      }, [])

      const itemsChanged = nextItems.length !== cell.items.length || nextItems.some((item, index) => item?.id !== cell.items[index]?.id)

      if (itemsChanged) {
        changed = true
        return {
          ...cell,
          items: nextItems,
        }
      }

      return cell
    })
  })

  return changed ? nextData : sourceData
}
